import { Button, Form, Modal, ModalProps, SelectProps, message } from 'antd';
import type { CheckboxValueType } from 'antd/es/checkbox/Group';
import { orderBy } from 'lodash';
import React, { useEffect, useState } from 'react';

import { useQueryDimensionData } from '@components/mapping-panel/api';
import { ConvivaEvent } from '@src/common/constant/enum';
import { RenderEventJourney } from '@src/emp-3.0/components/Sankey/Configurations';
import { FilterList } from '@src/emp-3.0/components/Sankey/GlobalFilter';
import RangePicker from '@src/emp-3.0/components/Sankey/GlobalFilter/RangePicker';
import { dimensionValueLabelRender } from '@src/emp-3.0/components/Sankey/components/CompactDualSelect';
import { useSankeyStore } from '@src/emp-3.0/components/Sankey/store';
import {
  APP_ID,
  updateEventBreakdownByLandingEvent,
  updateEventNameFiltersByLandingEvent,
} from '@src/emp-3.0/components/Sankey/utils';
import {
  IEventBreakdown,
  IInitSankeyRequestBody,
  ISankeyRequestBody,
  TDirection,
} from '@src/emp-3.0/types';
import { parseDataToOptions } from '@src/emp-3.0/utils/parse';

import styles from './index.less';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

export interface IInitDialog {
  modalProps?: ModalProps;
  interval?: string;
  onClose?: () => void;
  dimensionOptions?: SelectProps['options'];
  fixedDimensions: string[];
  onChange?: (
    value: CheckboxValueType[],
    index: number,
    filters: Array<CheckboxValueType[]>,
  ) => void;
  eventDimensionOptions: SelectProps['options'];
  onApply: (payloadPartial?: Partial<ISankeyRequestBody>) => void;
  initSankeyRequestBody: IInitSankeyRequestBody;
  calenderBoundry?: { start_time: number; end_time: number } | undefined;
}
const EVENT_NAME = 'event_name';

const InitDialog: React.FC<IInitDialog> = ({
  modalProps = {},
  dimensionOptions = [],
  onClose = () => {},
  eventDimensionOptions,
  onApply,
  initSankeyRequestBody,
  calenderBoundry,
}) => {
  const [form] = Form.useForm();

  const { setSankeyRequestBody, setShowConfigModal } = useSankeyStore((state) => state);
  const { filters, interval, landing_events } = initSankeyRequestBody;

  const [directionCurrentValue, setDirectionCurrentValue] = React.useState<TDirection>('forward');
  const [eventName, setEventName] = React.useState<string>(landing_events[0]?.event_name);
  const [eventData, setEventData] = React.useState(landing_events);
  const [filterCurrent, setFilterCurrent] = React.useState(filters);
  const [intervalCurrent, setIntervalCurrent] = React.useState(interval);
  const [eventList, setEventList] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(true);

  useEffect(() => {
    if (interval) {
      setIntervalCurrent(interval);
    }
  }, [interval]);

  useEffect(() => {
    setFilterCurrent(filters);
  }, [filters]);

  const disabledGenerateButton = React.useMemo(() => {
    return false;
  }, []);
  const [network_request_enabled, setNetworkRequestEnabled] = useState(0 as 0 | 1);
  const removeEmptyFilters = (filters) => {
    return filters.filter((item) => item.filter((x) => x.value.length).length);
  };

  const { data: eventNameData, isFetching: isEventNameLoading } = useQueryDimensionData(
    {
      fields: [EVENT_NAME],
      interval: intervalCurrent,
      filters: removeEmptyFilters(filterCurrent),
      params: {
        src: 'trends',
      },
      options: { enable: !!intervalCurrent },
    },
    500,
  );
  useEffect(() => {
    if (eventName === ConvivaEvent.conviva_network_request) {
      setNetworkRequestEnabled(1);
    } else {
      setNetworkRequestEnabled(0);
    }
  }, [eventName]);

  useEffect(() => {
    setEventData(landing_events);
  }, [landing_events]);

  useEffect(() => {
    if (!eventNameData) return;
    const eventList = eventNameData
      .filter((item) =>
        network_request_enabled === 0
          ? item[EVENT_NAME] !== ConvivaEvent.conviva_network_request
          : true,
      )
      .map((event) => {
        const name = event[EVENT_NAME];
        return {
          name,
        };
      });

    const orderEventList = orderBy(eventList, ['disabled'], ['desc']);
    setEventList(orderEventList.map((item) => item.name));
  }, [eventNameData, landing_events, network_request_enabled]);

  const landingEventNameOptions = parseDataToOptions(eventNameData || [], {
    label: EVENT_NAME,
    value: EVENT_NAME,
    labelRender: dimensionValueLabelRender,
  });

  const handleCancel = () => {
    setShowModal(false);
    onClose?.();
  };
  const handleReset = () => {};
  const validateFields = () => {
    let AppId = '';
    filterCurrent.forEach((item) =>
      item?.forEach((x) => {
        if (x.field === APP_ID) {
          AppId = x.value?.join('');
        }
      }),
    );
    if (!intervalCurrent) {
      message.error('Please select time range');
      return false;
    }

    if (!eventName) {
      message.error('Please fill eventName');
      return false;
    }
    return true;
  };
  const generateSankeyRequestBody = () => {
    const payload = {
      landing_events: eventData,
      direction: directionCurrentValue,
      interval: intervalCurrent,
      filters: filterCurrent,
      event_name_filters: updateEventNameFiltersByLandingEvent({
        landing_events: eventData,
        event_name_filters: eventList, // hp-6488 auto select all mapped events
      }),
      network_request_enabled,
    };

    const eventBreakDown = updateEventBreakdownByLandingEvent({
      event_breakdown: [],
      landing_event: eventData[0],
    });

    let updateSankeyRequestBody = {
      ...payload,
      event_breakdown: [{ event_name: '', breakdowns: [] }] as IEventBreakdown[],
    };
    if (eventBreakDown.length > 0) {
      updateSankeyRequestBody = {
        ...updateSankeyRequestBody,
        event_breakdown: [...eventBreakDown, { event_name: '', breakdowns: [] }],
      };
    }
    return updateSankeyRequestBody;
  };
  const handleGenerateButtonClick = () => {
    if (!validateFields()) return;
    setShowConfigModal(false);
    const payloadPartial = generateSankeyRequestBody();
    setSankeyRequestBody(payloadPartial);
    onApply?.(payloadPartial);
    setShowModal(false);
  };

  const handleChange = (value) => {
    setFilterCurrent(value);
  };

  const modalFooter = (
    <div className={styles.footer}>
      <Button
        type="primary"
        size="large"
        disabled={disabledGenerateButton}
        onClick={handleGenerateButtonClick}
        style={
          disabledGenerateButton
            ? {
                color: '#fff',
                backgroundColor: '#0046ff',
                borderColor: 'transparent',
                opacity: 0.4,
              }
            : undefined
        }
      >
        Generate Paths
      </Button>
    </div>
  );
  return (
    <Modal
      width={1024}
      destroyOnClose
      {...modalProps}
      onCancel={handleCancel}
      title={<div className={styles.dialogHeader}>Generate Paths</div>}
      footer={modalFooter}
      afterClose={() => {
        handleReset();
      }}
      closable={false}
      maskClosable={false}
      bodyStyle={{ padding: 0 }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="timeRange"
          label={<span style={{ fontWeight: 'bold' }}>Time Range</span>}
          style={{ marginBottom: '6px', marginTop: '8px' }}
        >
          <RangePicker
            interval={interval}
            onChange={(value) => {
              setIntervalCurrent(value);
            }}
            placement="left"
            durationLimit={14}
            calenderBoundry={calenderBoundry}
          />
        </Form.Item>
        <Form.Item
          name="appName"
          label={<span style={{ fontWeight: 'bold' }}>Global Filter (Optional)</span>}
          style={{ marginBottom: '6px' }}
        >
          <FilterList
            interval={intervalCurrent}
            dimensionOptions={dimensionOptions}
            fixedDimensions={['app_id']}
            filters={filters}
            onChange={handleChange}
          />
        </Form.Item>
        <div style={{ margin: '16px 0', width: '398px' }}>
          <RenderEventJourney
            isCollapse={false}
            landing_events={landing_events}
            direction={directionCurrentValue}
            interval={intervalCurrent}
            filters={filterCurrent}
            isEventNameLoading={isEventNameLoading}
            eventNameOptions={landingEventNameOptions}
            eventDimensionOptions={eventDimensionOptions}
            emitValues={(value) => {
              const { eventData, direction } = value;
              setEventName(eventData.event_name);
              setEventData([eventData]);
              setDirectionCurrentValue(direction);
            }}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default InitDialog;
